import { layoutToWorld } from '../core/coords';
import type { Layout } from '../data/types';
import type { Vec2, Vec3 } from '../geo/types';

export function itemAt<T>(items: ArrayLike<T>, index: number): T {
  const item = items[index];
  if (item === undefined) throw new Error('Tour path is missing a point.');
  return item;
}

function mix(a: Vec2, b: Vec2, t: number): Vec2 {
  return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];
}

function distance(a: Vec2, b: Vec2): number {
  return Math.hypot(b[0] - a[0], b[1] - a[1]);
}

/** Circular corner fillets, sampled closely enough to retain their bounded outline as polylines. */
export function roundedPath(
  points: readonly Vec2[],
  radii: readonly number[],
  spacing: number
): readonly Vec2[] {
  const path: Vec2[] = [itemAt(points, 0)];
  function line(end: Vec2): void {
    const start = itemAt(path, path.length - 1);
    const length = distance(start, end);
    if (length < 1e-7) return;
    const steps = Math.ceil(length / spacing);
    for (let i = 1; i <= steps; i++) path.push(mix(start, end, i / steps));
  }
  for (let i = 1; i < points.length - 1; i++) {
    const a = itemAt(points, i - 1),
      b = itemAt(points, i),
      c = itemAt(points, i + 1);
    const incomingLength = distance(a, b),
      outgoingLength = distance(b, c);
    const incoming: Vec2 = [(b[0] - a[0]) / incomingLength, (b[1] - a[1]) / incomingLength];
    const outgoing: Vec2 = [(c[0] - b[0]) / outgoingLength, (c[1] - b[1]) / outgoingLength];
    const turn = Math.acos(
      Math.max(-1, Math.min(1, incoming[0] * outgoing[0] + incoming[1] * outgoing[1]))
    );
    const radius = radii[i] ?? 0;
    if (!radius || turn < 1e-4) {
      line(b);
      continue;
    }
    const cut = Math.min(radius * Math.tan(turn / 2), incomingLength / 4, outgoingLength / 4);
    const actualRadius = cut / Math.tan(turn / 2);
    const start: Vec2 = [b[0] - incoming[0] * cut, b[1] - incoming[1] * cut];
    const sign = Math.sign(incoming[0] * outgoing[1] - incoming[1] * outgoing[0]);
    const centre: Vec2 = [
      start[0] - sign * incoming[1] * actualRadius,
      start[1] + sign * incoming[0] * actualRadius,
    ];
    const angle = Math.atan2(start[1] - centre[1], start[0] - centre[0]);
    line(start);
    const steps = Math.max(Math.ceil((actualRadius * turn) / spacing), Math.ceil(turn / 0.035));
    for (let step = 1; step <= steps; step++) {
      const t = angle + (sign * turn * step) / steps;
      path.push([centre[0] + Math.cos(t) * actualRadius, centre[1] + Math.sin(t) * actualRadius]);
    }
  }
  line(itemAt(points, points.length - 1));
  return path;
}

/** Begin halfway along a straight leg so the circuit's join has the same tangent on both sides. */
export function roundedCircuit(
  points: readonly Vec2[],
  radius: number | readonly number[],
  spacing: number
): readonly Vec2[] {
  const seam = mix(itemAt(points, points.length - 1), itemAt(points, 0), 0.5);
  const circuit = [seam, ...points, seam];
  return roundedPath(
    circuit,
    [0, ...points.map((_, i) => (typeof radius === 'number' ? radius : (radius[i] ?? 0))), 0],
    spacing
  );
}

export interface Gaze {
  readonly at: number;
  readonly span: number;
  readonly target: Vec3;
  readonly weight: number;
}

/** Blend viewing angles rather than distant targets, so a side glance cannot pass through the camera. */
function mixSight(point: Vec3, from: Vec3, to: Vec3, weight: number, range: number): Vec3 {
  const angle = (target: Vec3) => Math.atan2(target[2] - point[2], target[0] - point[0]);
  const pitch = (target: Vec3) =>
    Math.atan2(target[1] - point[1], Math.hypot(target[0] - point[0], target[2] - point[2]));
  const start = angle(from);
  const delta = Math.atan2(Math.sin(angle(to) - start), Math.cos(angle(to) - start));
  const yaw = start + delta * weight;
  const tilt = pitch(from) + (pitch(to) - pitch(from)) * weight;
  return [
    point[0] + Math.cos(yaw) * Math.cos(tilt) * range,
    point[1] + Math.sin(tilt) * range,
    point[2] + Math.sin(yaw) * Math.cos(tilt) * range,
  ];
}

function continuedForward(path: readonly Vec2[], point: Vec2, ahead: number): Vec2 {
  const end = itemAt(path, path.length - 1),
    before = itemAt(path, path.length - 2);
  const length = distance(before, end);
  const remaining = Math.max(ahead, distance(point, end));
  return [
    point[0] + ((end[0] - before[0]) / length) * remaining,
    point[1] + ((end[1] - before[1]) / length) * remaining,
  ];
}

export function guidedTargets(
  path: readonly Vec2[],
  height: (p: Vec2) => number,
  ahead: number,
  gazes: readonly Gaze[],
  closed = false,
  allowSideViews = false
): readonly Vec3[] {
  const distances = [0];
  for (let i = 1; i < path.length; i++)
    distances.push(itemAt(distances, i - 1) + distance(itemAt(path, i - 1), itemAt(path, i)));
  const length = itemAt(distances, distances.length - 1);
  let next = 1;
  return path.map((point, i): Vec3 => {
    const s = itemAt(distances, i);
    const goal = closed ? (s + ahead) % length : Math.min(length, s + ahead);
    if (goal < itemAt(distances, next - 1)) next = 1;
    while (next < path.length - 1 && itemAt(distances, next) < goal) next++;
    const from = itemAt(distances, next - 1),
      to = itemAt(distances, next);
    const forward =
      !closed && s + ahead > length
        ? continuedForward(path, point, ahead)
        : mix(itemAt(path, next - 1), itemAt(path, next), (goal - from) / (to - from));
    let target = layoutToWorld(forward, height(forward));
    const dx = forward[0] - point[0],
      dy = forward[1] - point[1];
    for (const gaze of gazes) {
      const separation = Math.abs(s - gaze.at);
      const within = Math.max(
        0,
        1 - (closed ? Math.min(separation, length - separation) : separation) / gaze.span
      );
      if (!within) continue;
      const tx = gaze.target[0] - point[0],
        ty = -gaze.target[2] - point[1];
      const facing = (dx * tx + dy * ty) / (Math.hypot(dx, dy) * Math.hypot(tx, ty));
      // A side glance fades before the landmark falls behind the walking direction.
      const alignment = allowSideViews ? 1 : Math.max(0, Math.min(1, (facing - 0.1) / 0.35));
      const fade = within * within * (3 - 2 * within);
      const weight = gaze.weight * fade * alignment;
      target = mixSight(layoutToWorld(point, height(point)), target, gaze.target, weight, ahead);
    }
    return target;
  });
}

export function landmarkSight(
  layout: Layout,
  id: string,
  fallback: Vec2,
  height: number,
  offset: Vec2 = [0, 0]
): Vec3 {
  const at = layout.landmarks.find((landmark) => landmark.id === id)?.pos ?? fallback;
  return layoutToWorld([at[0] + offset[0], at[1] + offset[1]], height);
}

export function gazeNear(
  path: readonly Vec2[],
  point: Vec2,
  target: Vec3,
  span: number,
  weight = 1
): Gaze {
  let at = 0,
    station = 0,
    nearest = Infinity;
  path.forEach((p, i) => {
    if (i) station += distance(itemAt(path, i - 1), p);
    const away = distance(point, p);
    if (away < nearest) {
      nearest = away;
      at = station;
    }
  });
  return { at, span, target, weight };
}
